"use client";
import { usePathname } from "next/navigation";
import { useEffect, useState } from "react";

const PROJECT_ID_KEY = "VERICAP_PROJECT_ID";

/**
 * Validates if a string is a valid UUID format
 * @param str - String to validate
 * @returns true if string matches UUID format (e.g., 5acb41f5-0400-4619-ac3d-3f7bc2ece69a)
 */
const isValidUUID = (str: string): boolean => {
  const uuidRegex =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return uuidRegex.test(str);
};

/**
 * Custom hook to extract project ID from the URL pathname with sessionStorage fallback
 * 1. First tries to get projectId from URL (second segment of path)
 * 2. Validates that the projectId is a valid UUID format
 * 3. Falls back to sessionStorage if not in URL
 * 4. Stores projectId in sessionStorage when found in URL
 * 5. Clears sessionStorage when navigating to dashboard or root routes
 * 6. Updates sessionStorage when switching projects
 * 7. Reactively updates when sessionStorage changes
 * Example: /manage-project/5acb41f5-0400-4619-ac3d-3f7bc2ece69a/overview -> returns "5acb41f5-0400-4619-ac3d-3f7bc2ece69a"
 */
const useProjectId = (): string => {
  const pathname = usePathname();
  const [projectId, setProjectId] = useState<string>("");

  // Function to get and validate projectId from sessionStorage
  const getStoredProjectId = (): string => {
    if (typeof window !== "undefined") {
      const storedProjectId = sessionStorage.getItem(PROJECT_ID_KEY);
      if (storedProjectId && isValidUUID(storedProjectId)) {
        return storedProjectId;
      }
    }
    return "";
  };

  useEffect(() => {
    // Get projectId from URL (second segment)
    const urlProjectId = pathname.split("/")[2];

    // Check if user is on dashboard, root routes, or admin routes - clear sessionStorage
    const isDashboardRoute =
      pathname === "/dashboard" ||
      pathname === "/" ||
      pathname.startsWith("/dashboard");

    const isAdminRoute = pathname.includes("/admin/");

    if (isDashboardRoute || isAdminRoute) {
      // Clear sessionStorage when on dashboard or admin routes
      if (typeof window !== "undefined") {
        sessionStorage.removeItem(PROJECT_ID_KEY);
      }
      setProjectId("");
      return;
    }

    // If projectId exists in URL and is a valid UUID
    if (urlProjectId && isValidUUID(urlProjectId)) {
      // Store in sessionStorage
      if (typeof window !== "undefined") {
        sessionStorage.setItem(PROJECT_ID_KEY, urlProjectId);
      }
      setProjectId(urlProjectId);
    } else {
      // Fallback to sessionStorage if not in URL or invalid UUID
      // COMMENTED OUT: Don't fallback to sessionStorage for now
      // const storedId = getStoredProjectId();
      // setProjectId(storedId);
      setProjectId("");
    }
  }, [pathname]);

  // Listen for sessionStorage changes (for same-tab updates)
  //*** uncomment for session logic ***
  // useEffect(() => {
  //   const handleStorageChange = () => {
      // const storedId = getStoredProjectId();
      // // Only update if there's no projectId in URL (we're on a route without ID)
      // const urlProjectId = pathname.split("/")[2];
      // if (!urlProjectId || !isValidUUID(urlProjectId)) {
      //   setProjectId(storedId);
      // }
    // };

    // window.addEventListener("sessionStorageChange", handleStorageChange);

    // Polling as fallback (checks every 500ms for sessionStorage changes)
    // const intervalId = setInterval(() => {
    //   const currentStored = getStoredProjectId();
    //   const urlProjectId = pathname.split("/")[2];

    //   // Only update if we're not on a URL with a valid projectId
    //   if (
    //     (!urlProjectId || !isValidUUID(urlProjectId)) &&
    //     currentStored !== projectId
    //   ) {
    //     setProjectId(currentStored);
    //   }
    // }, 500);

    // return () => {
    //   window.removeEventListener("sessionStorageChange", handleStorageChange);
      // clearInterval(intervalId);
  //   };
  // }, [pathname, projectId]);

  return projectId;
};

export default useProjectId;
